import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, ArrowLeft } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api, photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ContactLinks, UnlockButton } from "@/components/UnlockContacts";
import { ReportButton } from "@/components/ReportButton";
import { MessageButton } from "@/components/MessageButton";
import { Seo } from "@/components/Seo";

const Spec = ({ label, value }) => (
  <div className="border-b border-white/5 py-3 flex justify-between text-sm">
    <span className="text-neutral-500">{label}</span>
    <span className="text-neutral-200">{value || "—"}</span>
  </div>
);

export default function HostessDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [h, setH] = useState(null);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => { api.get(`/hostesses/${id}`).then((r) => setH(r.data)).catch(() => setH(false)); }, [id]);

  if (h === null) return <p className="eyebrow text-center py-40">Caricamento…</p>;
  if (!h) return <p className="font-serif text-3xl text-center py-40" data-testid="hostess-not-found">Profilo non trovato</p>;
  const p = h.profile || {};
  const photos = p.photos || [];

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-12" data-testid="hostess-detail-page">
      <Seo title="Profilo | YourHostess" noindex />
      <Link to="/cerca" className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-[#D4AF37] flex items-center gap-2" data-testid="hostess-back-link"><ArrowLeft className="w-3 h-3" /> Torna alla ricerca</Link>
      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-7">
          <button onClick={() => photos.length && setZoom(true)} className="block w-full aspect-[4/5] bg-[#181B20] overflow-hidden border border-white/5" data-testid="hostess-main-photo">
            {photos[active] ? <img src={photoUrl(photos[active])} alt={h.name} className="w-full h-full object-cover" /> : <span className="font-serif text-8xl text-neutral-700">{h.name[0]}</span>}
          </button>
          {photos.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-2">
              {photos.map((ph, i) => (
                <button key={ph} onClick={() => setActive(i)} className={`aspect-square overflow-hidden border ${i === active ? "border-[#D4AF37]" : "border-transparent opacity-60 hover:opacity-100"} transition-opacity`} data-testid={`hostess-thumb-${i}`}>
                  <img src={photoUrl(ph)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <aside className="lg:col-span-5 lg:sticky lg:top-28 self-start fade-up">
          <div className="flex flex-wrap gap-2">{(p.categories || []).map((c) => <span key={c} className="eyebrow border border-[#D4AF37]/30 px-2 py-1">{c}</span>)}</div>
          <h1 className="font-serif text-5xl lg:text-6xl mt-5" data-testid="hostess-name">{h.name}</h1>
          <p className="flex items-center gap-2 text-neutral-400 mt-3" data-testid="hostess-city"><MapPin className="w-4 h-4 text-[#D4AF37]" />{p.city || "—"}</p>
          {user?.id !== h.id && <div className="mt-4"><ReportButton targetType="hostess" targetId={h.id} label="Segnala profilo" /></div>}
          <p className="text-neutral-300 leading-relaxed mt-8">{p.bio || "Nessuna biografia inserita."}</p>
          <div className="mt-8">
            <Spec label="Età" value={p.age && `${p.age} anni`} />
            <Spec label="Altezza" value={p.height && `${p.height} cm`} />
            <Spec label="Taglia" value={p.size} />
            <Spec label="Scarpe" value={p.shoes} />
            <Spec label="Occhi" value={p.eyes} />
            <Spec label="Capelli" value={p.hair} />
            <Spec label="Lingue" value={(p.languages || []).join(", ")} />
          </div>
          {user?.role === "client" ? (
            <div className="mt-10 border border-white/10 bg-[#121418] p-6" data-testid="hostess-contacts-box">
              <p className="eyebrow">Contatti</p>
              <div className="mt-4">
                {h.contacts ? (
                  <div className="space-y-4">
                    <ContactLinks contacts={h.contacts} testid="hostess-contacts" />
                    <MessageButton hostessId={h.id} className="btn-gold w-full" testid="hostess-message-button" />
                  </div>
                )
                  : <UnlockButton hostess={h} onUnlocked={(contacts) => setH({ ...h, contacts })} className="btn-gold w-full" testid="hostess-unlock-button" />}
              </div>
            </div>
          ) : !user ? (
            <Link to="/accedi?mode=register&role=client" className="btn-gold w-full mt-10" data-testid="hostess-register-cta">Registrati come cliente per prenotare</Link>
          ) : null}
        </aside>
      </div>
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-3xl p-0 bg-black border-white/10" data-testid="hostess-photo-modal">
          {photos[active] && <img src={photoUrl(photos[active])} alt="" className="w-full max-h-[85vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
